import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../../../shared/lib/apiClient';
import SyncBanner from '../../../shared/components/SyncBanner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) =>
  n == null ? '--' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (str) => {
  if (!str) return '--';
  return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr + 'T00:00:00') - new Date()) / 86400000);
};

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const STATUS_META = {
  open:                { label: 'Open',     badge: 'bg-blue-50 text-blue-700 border-blue-100',       dot: 'bg-blue-400' },
  partially_received:  { label: 'Partial',  badge: 'bg-amber-50 text-amber-700 border-amber-100',    dot: 'bg-amber-400' },
  received:            { label: 'Received', badge: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500' },
  closed:              { label: 'Closed',   badge: 'bg-zinc-100 text-zinc-500 border-zinc-200',      dot: 'bg-zinc-400' },
  cancelled:           { label: 'Cancelled',badge: 'bg-red-50 text-red-600 border-red-100',          dot: 'bg-red-400' },
};

const StatusBadge = ({ status }) => {
  const { label, badge, dot } = STATUS_META[status] || STATUS_META.open;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border font-medium ${badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Match type display
// ---------------------------------------------------------------------------

const MATCH_META = {
  full_match:               { label: 'Matched',       color: 'text-emerald-600', icon: 'text-emerald-500' },
  match_with_discrepancies: { label: 'Discrepancies',  color: 'text-amber-600',   icon: 'text-amber-500' },
  partial_match:            { label: 'Partial',        color: 'text-blue-600',    icon: 'text-blue-500' },
  no_match:                 { label: 'No Match',       color: 'text-zinc-400',    icon: 'text-zinc-400' },
};

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

const StatCard = ({ label, value, sub, highlight }) => (
  <div className={`bg-white border rounded-xl p-4 sm:p-5 ${highlight ? 'border-amber-200 bg-amber-50/30' : 'border-zinc-200'}`}>
    <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium truncate">{label}</p>
    <p className={`mt-1.5 text-2xl sm:text-3xl font-semibold tabular-nums ${highlight ? 'text-amber-600' : 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-xs mt-1 text-zinc-400">{sub}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// PO Detail Drawer
// ---------------------------------------------------------------------------

const PODrawer = ({ po, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!po) return;
    setLoading(true);
    apiFetch(`/api/purchase-orders/${po.id}`)
      .then(r => r.json())
      .then(data => { setDetail(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [po]);

  if (!po) return null;

  const deliveryDays = detail?.delivery_date ? daysUntil(detail.delivery_date) : null;
  const allDiscrepancies = (detail?.matched_bills || []).flatMap(b => (b.po_match?.discrepancies || []).map(d => ({ ...d, bill: b })));

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-[slideIn_0.2s_ease-out]">
        {/* Header */}
        <div className="shrink-0 border-b border-zinc-100 px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-zinc-900 tracking-tight">{po.po_number || 'Purchase Order'}</h2>
                <StatusBadge status={po.status} />
              </div>
              <p className="text-sm text-zinc-500 mt-1">{po.vendor_name}</p>
            </div>
            <button onClick={onClose} className="p-1.5 -mr-1.5 -mt-1 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
            </div>
          ) : detail ? (
            <div className="px-6 py-5 space-y-6">
              {/* Key details — compact row */}
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs border border-zinc-100 rounded-lg px-4 py-3 bg-zinc-50/50">
                <div><span className="text-zinc-400">Total</span> <span className="text-zinc-900 font-semibold ml-1">{fmt(detail.total_amount)}</span></div>
                <div><span className="text-zinc-400">Ordered</span> <span className="text-zinc-700 font-medium ml-1">{fmtDate(detail.order_date)}</span></div>
                <div>
                  <span className="text-zinc-400">Delivery</span>
                  <span className={`font-medium ml-1 ${deliveryDays != null && deliveryDays < 0 ? 'text-red-500' : 'text-zinc-700'}`}>{fmtDate(detail.delivery_date)}</span>
                  {deliveryDays != null && deliveryDays < 0 && <span className="text-[10px] text-red-400 ml-1">({Math.abs(deliveryDays)}d overdue)</span>}
                </div>
                {detail.payment_terms && <div><span className="text-zinc-400">Terms</span> <span className="text-zinc-700 font-medium ml-1">{detail.payment_terms}</span></div>}
                <div><span className="text-zinc-400">Items</span> <span className="text-zinc-700 font-medium ml-1">{detail.line_items?.length || 0}</span></div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Line Items</h3>
                  <span className="text-[10px] text-zinc-300">{detail.line_items?.length || 0} items</span>
                </div>
                <div className="border border-zinc-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-zinc-50/80 border-b border-zinc-100">
                        <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium w-20">Item #</th>
                        <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Description</th>
                        <th className="text-right py-2.5 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium w-14">Qty</th>
                        <th className="text-right py-2.5 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium w-20">Price</th>
                        <th className="text-right py-2.5 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium w-24">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.line_items || []).map((li, i) => (
                        <tr key={li.id} className={`border-b border-zinc-50 ${i % 2 === 1 ? 'bg-zinc-50/30' : ''}`}>
                          <td className="py-2.5 px-3 text-zinc-500 font-mono text-[11px]">{li.item_number || '--'}</td>
                          <td className="py-2.5 px-3 text-zinc-700">{li.description}</td>
                          <td className="py-2.5 px-3 text-right text-zinc-600 tabular-nums">{li.quantity_ordered} <span className="text-zinc-400">{li.unit_of_measure}</span></td>
                          <td className="py-2.5 px-3 text-right text-zinc-600 tabular-nums">{fmt(li.unit_price)}</td>
                          <td className="py-2.5 px-3 text-right text-zinc-800 font-medium tabular-nums">{fmt(li.extended_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-zinc-200 bg-zinc-50/50">
                        <td colSpan={3} />
                        <td className="py-2.5 px-3 text-right text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Total</td>
                        <td className="py-2.5 px-3 text-right text-sm font-semibold text-zinc-900 tabular-nums">{fmt(detail.total_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Invoice Matching */}
              <div>
                <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-3">Invoice Matching</h3>
                {(detail.matched_bills || []).length === 0 ? (
                  <div className="border border-dashed border-zinc-200 rounded-xl py-8 text-center">
                    <p className="text-xs text-zinc-400">No invoices matched to this PO yet</p>
                    <p className="text-[10px] text-zinc-300 mt-1">Invoices referencing {po.po_number} will appear here automatically</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {detail.matched_bills.map(bill => {
                      const match = bill.po_match;
                      const meta = MATCH_META[match?.match_type] || MATCH_META.no_match;
                      const discCount = match?.discrepancies?.length || 0;

                      return (
                        <div key={bill.id} className="border border-zinc-200 rounded-xl overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3 bg-zinc-50/50">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-zinc-800">#{bill.bill_number || '--'}</span>
                              <span className="text-[11px] text-zinc-400">{fmtDate(bill.bill_date)}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-zinc-800 tabular-nums">{fmt(bill.total_amount)}</span>
                              <span className={`text-[11px] font-semibold ${meta.color}`}>
                                {match?.match_type === 'full_match' && (
                                  <span className="inline-flex items-center gap-1">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={meta.icon}><path d="M20 6L9 17l-5-5" /></svg>
                                    {meta.label}
                                  </span>
                                )}
                                {match?.match_type === 'match_with_discrepancies' && `${discCount} ${discCount === 1 ? 'discrepancy' : 'discrepancies'}`}
                                {match?.match_type === 'partial_match' && `${match.matched_lines}/${match.total_lines} matched`}
                              </span>
                            </div>
                          </div>

                          {/* Discrepancies */}
                          {discCount > 0 && (
                            <div className="border-t border-zinc-100 px-4 py-3 space-y-3">
                              {match.discrepancies.map((d, i) => (
                                <div key={i} className="space-y-1">
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className={`shrink-0 text-[10px] uppercase tracking-wider font-semibold w-[52px] ${d.type === 'price' ? 'text-red-500' : 'text-amber-500'}`}>
                                      {d.type === 'price' ? 'Price' : 'Qty'}
                                    </span>
                                    <span className="text-zinc-600 font-mono text-[11px] truncate min-w-0">{d.item}</span>
                                    <div className="flex items-center gap-1.5 shrink-0 ml-auto tabular-nums">
                                      <span className="text-zinc-400">{d.type === 'price' ? fmt(d.expected) : d.expected}</span>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-zinc-300"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                                      <span className={`font-medium ${d.type === 'price' && d.diff > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                        {d.type === 'price' ? fmt(d.actual) : d.actual}
                                      </span>
                                      {d.pct_change && <span className="text-[10px] text-red-400 ml-0.5">({d.pct_change})</span>}
                                    </div>
                                  </div>
                                  {d.explanation && (
                                    <p className="text-[10px] text-zinc-400 leading-snug pl-[64px]">{d.explanation}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">Failed to load details</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Table Row
// ---------------------------------------------------------------------------

const PORow = ({ po, onClick }) => {
  const deliveryDays = po.delivery_date ? daysUntil(po.delivery_date) : null;
  const deliveryWarning = deliveryDays != null && deliveryDays < 0 && po.status === 'open';

  return (
    <tr onClick={onClick} className="group border-b border-zinc-50 cursor-pointer hover:bg-zinc-50/80 transition-colors">
      <td className="py-3.5 px-4">
        <span className="text-sm font-medium text-zinc-800">{po.po_number || '--'}</span>
      </td>
      <td className="py-3.5 px-4">
        <span className="text-sm text-zinc-600">{po.vendor_name || '--'}</span>
      </td>
      <td className="py-3.5 px-4">
        <span className="text-sm text-zinc-500">{fmtDate(po.order_date)}</span>
      </td>
      <td className="py-3.5 px-4">
        <span className={`text-sm ${deliveryWarning ? 'text-red-500 font-medium' : 'text-zinc-500'}`}>
          {fmtDate(po.delivery_date)}
        </span>
        {deliveryWarning && <span className="text-[10px] text-red-400 block">{Math.abs(deliveryDays)}d overdue</span>}
      </td>
      <td className="py-3.5 px-4 text-right">
        <span className="text-sm text-zinc-500 tabular-nums">{po.line_item_count}</span>
      </td>
      <td className="py-3.5 px-4 text-right">
        <span className="text-sm font-medium text-zinc-800 tabular-nums">{fmt(po.total_amount)}</span>
      </td>
      <td className="py-3.5 px-4 text-center">
        <StatusBadge status={po.status} />
      </td>
      <td className="pr-3 py-3.5 text-right">
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="inline text-zinc-300 group-hover:text-zinc-400 transition-colors">
          <path d="M4.5 2.5l3 3-3 3" />
        </svg>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------

const PurchaseOrdersView = ({ loaded }) => {
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);

  const fetchPOs = useCallback(async () => {
    try {
      setError(null);
      const res = await apiFetch('/api/purchase-orders');
      if (!res.ok) throw new Error('Failed to load purchase orders');
      const data = await res.json();
      setPOs(data.purchase_orders || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loaded) fetchPOs();
  }, [loaded, fetchPOs]);

  const filtered = pos.filter(po => {
    if (statusFilter && po.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (po.po_number || '').toLowerCase().includes(q) || (po.vendor_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const statusCounts = {};
  pos.forEach(po => { statusCounts[po.status] = (statusCounts[po.status] || 0) + 1; });

  const totalOpen = pos.filter(p => p.status === 'open').reduce((s, p) => s + (Number(p.total_amount) || 0), 0);
  const overdueCount = pos.filter(p => p.status === 'open' && p.delivery_date && daysUntil(p.delivery_date) < 0).length;

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Purchase Orders</h1>
          <p className="text-sm text-zinc-400 mt-0.5">{pos.length} total{totalOpen > 0 ? ` \u00B7 ${fmt(totalOpen)} open` : ''}</p>
        </div>
      </div>

      {/* Sync banner */}
      {!loading && (
        <SyncBanner
          entity="purchase_orders"
          label="purchase orders"
          onSynced={fetchPOs}
        />
      )}

      {/* Stats */}
      {pos.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Open" value={statusCounts.open || 0} sub={totalOpen > 0 ? fmt(totalOpen) : null} />
          <StatCard label="Partial" value={statusCounts.partially_received || 0} highlight={statusCounts.partially_received > 0} />
          <StatCard label="Received" value={statusCounts.received || 0} />
          <StatCard label="Overdue" value={overdueCount} highlight={overdueCount > 0} sub={overdueCount > 0 ? 'past delivery date' : null} />
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { key: null, label: 'All' },
            ...Object.entries(STATUS_META).map(([key, { label }]) => ({ key, label })),
          ].map(({ key, label }) => {
            const count = key === null ? pos.length : (statusCounts[key] || 0);
            if (key !== null && count === 0) return null;
            const active = statusFilter === key;
            return (
              <button
                key={key || 'all'}
                onClick={() => setStatusFilter(active ? null : key)}
                className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-medium transition-colors ${
                  active ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50 hover:text-zinc-700'
                }`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
        <div className="relative sm:ml-auto">
          <input
            type="text"
            placeholder="Search POs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full sm:w-64 pl-8 pr-3 py-1.5 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-zinc-300 bg-white"
          />
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-16 text-red-500 text-sm">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24">
          <svg className="mx-auto mb-3 text-zinc-200" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M4 2h8a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" />
            <path d="M6 5h4M6 8h4M6 11h2" />
          </svg>
          <p className="text-sm text-zinc-400">No purchase orders found</p>
          <p className="text-xs text-zinc-300 mt-1">Upload a PO document in the Inbox to get started</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50/50">
                {[
                  { label: 'PO #', align: 'left' },
                  { label: 'Vendor', align: 'left' },
                  { label: 'Ordered', align: 'left' },
                  { label: 'Delivery', align: 'left' },
                  { label: 'Items', align: 'right' },
                  { label: 'Amount', align: 'right' },
                  { label: 'Status', align: 'center' },
                  { label: '', align: 'right' },
                ].map((col, i) => (
                  <th key={i} className={`py-2.5 px-4 text-[10px] uppercase tracking-widest text-zinc-400 font-medium text-${col.align} ${i === 7 ? 'w-8' : ''}`}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((po) => (
                <PORow key={po.id} po={po} onClick={() => setSelectedPO(po)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer — portalled to body so it's not clipped by overflow containers */}
      {selectedPO && createPortal(
        <PODrawer po={selectedPO} onClose={() => setSelectedPO(null)} />,
        document.body
      )}
    </div>
  );
};

export default PurchaseOrdersView;
